/**
 * The route settings screen's data contract — the complex UI's extra level.
 *
 * Complex reaches the same source and destination settings the other screens
 * cover; what it adds is a level ABOVE them, for the route that groups several
 * sources. That level is this screen and nothing else.
 *
 * FOUR TABS, NO PRUNING, NO DELETE. The screen this replaced built its strip
 * from a flat object of Detail / Filters / Tracking / Shorteners with no
 * condition on any of them — unlike the source and destination strips, nothing
 * is hidden per type. Delete lives on the route row in the list, so the screen
 * has a save/cancel footer and no danger zone.
 */

import {
  type SettingsOption,
  type SettingsSection,
} from "@/shadcn/components/settings/settings-form"

import {
  type Constants,
  type LegacyRecord,
  FILTER_PATHS,
  attachErrors,
  choice,
  filterFields,
  options,
  text,
} from "@/lib/settings-sections"

export type RouteSectionsContext = {
  constants: Constants
  /** `state.shorteners` and `state.domains`, which share one select. */
  shorteners: LegacyRecord[]
  domains: LegacyRecord[]
  /** `ConstantMixin.getShortenServiceByCode`, passed rather than imported. */
  shortenServiceName: (ssID: number) => string
  /** `user.detail.defaultSaId` / `defaultPdId`, to name the system default. */
  defaultSaId: number
  defaultPdId: number
  /** Field id -> the message the last edit to it was refused with. */
  fieldErrors?: Record<string, string>
  /** `validationTree(app)` — the server's complaint about the last write. */
  validation?: LegacyRecord | null
}

const SECTION_LABEL: Record<string, string> = {
  detail: "General",
  filters: "Filters",
  tracking: "Tracking",
  shorteners: "Shorteners",
}

/** Section id -> its validation keys, for the tab's error badge. */
export const ROUTE_ERROR_KEYS: Record<string, string[]> = {
  detail: ["name"],
  filters: [
    "filtersTitle",
    "filtersCategory",
    "filtersContent",
    "filtersAuthor",
    "filtersLink",
    "filterBehavior",
    "andFilter",
    "orFilter",
    "notFilter",
  ],
  tracking: ["urlAppend"],
  /* `RouteShortenersPanel` renders a FieldError for `paID` beside `saID`,
     though what it writes is `pdID`. Both are listed so an error on either
     spelling reaches the tab. */
  shorteners: ["saID", "paID", "pdID"],
}

/** The fields whose one `name` stands for several stored values. */
export const ROUTE_COMPOSITE_PATHS: Record<string, string[]> = {
  "route.filters": FILTER_PATHS,
  "route.saID": ["saID", "pdID"],
}

/* ──────────────────────────────────────────────────────────── shorteners ── */

/** What "Use system default" resolves to, so the option can name it. */
function defaultTargetName(ctx: RouteSectionsContext): string {
  if (ctx.defaultSaId === 0) return "No Shortener"
  if (ctx.defaultSaId === 1) return "dlvr.it"

  if (ctx.defaultSaId > 1) {
    const shortener = (ctx.shorteners ?? []).find(
      (entry) => entry.id === ctx.defaultSaId
    )

    return shortener ? text(shortener.name) : ""
  }

  if (ctx.defaultPdId > 1) {
    const domain = (ctx.domains ?? []).find((entry) => entry.id === ctx.defaultPdId)

    return domain ? text(domain.domain) : ""
  }

  return ""
}

/**
 * ONE SELECT, THREE OUTCOMES — one more than the destination's.
 *
 * A route may defer to the account's own default, which neither a shortener id
 * nor a domain id can express, so `sysdef` is its own option and writes NULL
 * to both ids. `RouteShortenersPanel.updateField` reads the option value the
 * same way.
 */
function linkTargetOptions(ctx: RouteSectionsContext): SettingsOption[] {
  const list: SettingsOption[] = [
    { value: "sysdef", label: `Use system default (${defaultTargetName(ctx)})` },
    { value: "shortener-0", label: "Don't shorten links" },
    { value: "shortener-1", label: "dlvr.it" },
  ]

  for (const shortener of ctx.shorteners ?? []) {
    const ssID = parseInt(String(shortener.ssID ?? ""), 10) || 0

    list.push({
      value: `shortener-${shortener.id}`,
      label:
        ssID > 1
          ? `${ctx.shortenServiceName(ssID)} - ${text(shortener.name)}`
          : text(shortener.name),
    })
  }

  for (const domain of ctx.domains ?? []) {
    list.push({ value: `domain-${domain.id}`, label: text(domain.domain) })
  }

  return list
}

/** Which of the three the stored pair means. Legacy's own precedence. */
export function linkTargetValue(route: LegacyRecord): string {
  const domainId = route?.pdID
  const shortenerId = route?.saID

  if (domainId !== null && domainId !== undefined && domainId > 1) {
    return `domain-${domainId}`
  }

  if (shortenerId !== null && shortenerId !== undefined) {
    return `shortener-${shortenerId}`
  }

  return "sysdef"
}

/* ────────────────────────────────────────────────────────────── the tabs ── */

/**
 * Tracking.
 *
 * TWO FIELDS SHARE ONE DOT PATH, deliberately. `route.urlAppend` holds either
 * a tracking string or NULL, and null is what "use my account's default" means
 * — there is no second flag. So the toggle and the textarea are two views of
 * the same value: the toggle is on when it is null, and turning it off writes
 * an empty string to hand the textarea something to edit. `defaultToggle` does
 * exactly this.
 *
 * §6 lists only the toggle for this tab because its table is keyed by path and
 * the two collapse to one row. The textarea is in the design regardless —
 * `route-settings.tsx` says so in as many words.
 */
function trackingSection(route: LegacyRecord): SettingsSection {
  const usingDefault = route?.urlAppend === null || route?.urlAppend === undefined

  return {
    id: "tracking",
    label: SECTION_LABEL.tracking,
    fields: [
      {
        id: "url-append-default",
        name: "route.urlAppend",
        label: "Use my account's default tracking",
        kind: "toggle",
        checked: usingDefault,
      },
      {
        id: "url-append",
        name: "route.urlAppend",
        label: "Analytics",
        kind: "textarea",
        value: usingDefault ? "" : text(route.urlAppend),
        disabled: usingDefault,
        help: "Add analytics to your links for enhanced reporting from your site analytics service.",
        span: "full",
      },
      {
        id: "tracking-variables",
        label: "Variables you can use",
        /*
         * A LOOKUP, NOT PROSE, which is why it is a table rather than a
         * tooltip. Legacy renders the same list inline under the field for the
         * same reason.
         */
        kind: "reference",
        refRows: [
          { name: "[feedurl]", desc: "If your source is a feed, that feed's URL" },
          { name: "[sourcename]", desc: "The name of your source (URL-encoded)" },
          { name: "[desttype]", desc: "Where the item was posted, e.g. facebook" },
          { name: "[destname]", desc: "The account name on your output (URL-encoded)" },
          { name: "[routename]", desc: "The name of your route (URL-encoded)" },
        ],
        span: "full",
      },
    ],
  }
}

/* ────────────────────────────────────────────────────────── the builder ── */

export function buildRouteSections(
  route: LegacyRecord | null | undefined,
  ctx: RouteSectionsContext
): SettingsSection[] {
  if (!route || route.id === undefined) return []

  const sections: SettingsSection[] = [
    {
      id: "detail",
      label: SECTION_LABEL.detail,
      fields: [
        {
          id: "name",
          name: "route.name",
          label: "Route name",
          kind: "input",
          value: text(route.name),
          required: true,
        },
      ],
    },
    {
      id: "filters",
      label: SECTION_LABEL.filters,
      /* The same tab the source screen has, from the same builder. */
      fields: filterFields(route, "route"),
    },
    trackingSection(route),
    {
      id: "shorteners",
      label: SECTION_LABEL.shorteners,
      fields: [
        {
          id: "sa-id",
          name: "route.saID",
          label: "Link shortening service or custom domain",
          kind: "select",
          options: linkTargetOptions(ctx),
          value: linkTargetValue(route),
        },
      ],
    },
  ]

  return attachErrors(sections, {
    tree: ctx.validation,
    prefix: "route",
    fieldErrors: ctx.fieldErrors,
  })
}
